import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../../lib/apiClient.js';
import { queryKeys } from '../../lib/queryKeys.js';
import type { AssistantProfile, AssistantProfileUpdateBody } from '../../lib/types.js';

const PROFILE_PATH = '/api/assistant-profile';

interface AssistantProfileLogic {
  isLoading: boolean;
  data: AssistantProfile | undefined;
  error: Error | null;
}

export const useAssistantProfileLogic = (tenantId: string | undefined): AssistantProfileLogic => {
  const query = useQuery<AssistantProfile>({
    queryKey: queryKeys.assistantProfile(tenantId ?? ''),
    queryFn: () => apiRequest<AssistantProfile>(PROFILE_PATH),
    enabled: Boolean(tenantId),
  });
  return { isLoading: query.isLoading, data: query.data, error: query.error };
};

interface SaveProfileLogic {
  isSubmitting: boolean;
  error: Error | null;
  /** True after a successful save until the next edit. */
  saved: boolean;
  clearSaved: () => void;
  save: (body: AssistantProfileUpdateBody) => Promise<AssistantProfile>;
}

export const useSaveAssistantProfileLogic = (tenantId: string | undefined): SaveProfileLogic => {
  const queryClient = useQueryClient();
  const [saved, setSaved] = useState(false);

  const mutation = useMutation<AssistantProfile, Error, AssistantProfileUpdateBody>({
    mutationFn: (body) => apiRequest<AssistantProfile>(PROFILE_PATH, { method: 'PUT', body }),
    onSuccess: (fresh) => {
      queryClient.setQueryData(queryKeys.assistantProfile(tenantId ?? ''), fresh);
      setSaved(true);
    },
  });

  return {
    isSubmitting: mutation.isPending,
    error: mutation.error,
    saved,
    clearSaved: () => setSaved(false),
    save: (body) => mutation.mutateAsync(body),
  };
};
